import { useContext, useEffect, useMemo } from 'react';
import { Col, Row, Timeline } from 'antd';
import { useParams } from 'react-router-dom';
import ButtonIcon from 'components/buttonIcon';
import { Title } from 'pages/Analytics/components/ActiveUsersCollapse/styles';
import { SectionCard } from 'pages/Analytics/components/AnalyticsContent/styles';
import { SessionContext } from 'contexts/session';
import { ActivityLogContext } from 'contexts/activityLog';
import { ActivityLogContainerMobile } from './styles';
import ActivityLogItemMobile from './ActivityLogMobile/ActivityLogItemMobile';
import ActivityLogDrawer from './ActivityLogMobile/ActivityLogDrawer';
import { useFlags } from 'launchdarkly-react-client-sdk';

const ActivityLogContentMobile = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const { showActivityLogDrawer, setShowActivityLogDrawer } = useContext(SessionContext);
  const { isStaff } = useContext(SessionContext);
  const { actions, fetching, isEmpty, table, filters, isPollingCircuitOpen } =
    useContext(ActivityLogContext);
  const { dashboardAccountPlaylistAssignment } = useFlags();
  const showAccountPlaylistActions = Boolean(dashboardAccountPlaylistAssignment);

  // When the LD flag is OFF, hide account-playlist activity entries so the feature is invisible.
  // Client-side filtering is a safe guard in case server-side filtering isn't available.
  const visibleItems = useMemo(() => {
    const items = table?.items ?? [];
    if (showAccountPlaylistActions) return items;
    return items.filter((item) => !item.action?.startsWith('account.playlist.'));
  }, [showAccountPlaylistActions, table?.items]);

  useEffect(() => {
    if (isStaff) {
      actions.onChangeFilters([
        { name: 'account_id', value: id },
        { name: 'from_date', value: null },
        { name: 'to_date', value: null },
      ]);
      actions.onRetrieveActivityLogUsers(id);
    } else {
      actions.onChangeFilters([
        { name: 'from_date', value: null },
        { name: 'to_date', value: null },
      ]);
      actions.onRetrieveActivityLogUsers();
    }
  }, []);

  useEffect(() => {
    if (!showActivityLogDrawer) {
      if (isStaff) {
        actions.onResetActivities(id);
        actions.onRetrieveActivityLogUsers(id);
      } else {
        actions.onResetActivities();
        actions.onRetrieveActivityLogUsers();
      }
    }
  }, [showActivityLogDrawer]);

  useEffect(() => {
    if (showAccountPlaylistActions) return;
    if (fetching) return;
    if (!actions?.onChangeFilter) return;

    const loadedCount = table?.items?.length ?? 0;
    const totalCount = table?.count ?? 0;
    if (!loadedCount) return;
    if (visibleItems.length > 0) return;
    if (loadedCount >= totalCount) return;

    // If all fetched items are filtered out (flag OFF), request the next page to avoid an empty UI
    // when lazy loading is based on scroll and there is nothing to scroll.
    actions.onChangeFilter({
      name: 'page',
      value: (parseInt(filters.page) + 1).toString(),
    });
  }, [
    actions,
    fetching,
    filters.page,
    showAccountPlaylistActions,
    table?.count,
    table?.items?.length,
    visibleItems.length,
  ]);

  return (
    <SectionCard padding={'20px'} rounded dashboard shadow>
      <header>
        <Row>
          <Col xs={20} style={{ paddingTop: '5px', lineHeight: '26px' }}>
            <Title style={{ fontSize: '20px' }}>Account recent activity</Title>
          </Col>
          <Col xs={4}>
            <ButtonIcon
              $terciary
              icon={'ArrowRight'}
              onClick={() => setShowActivityLogDrawer(true)}
              style={{ float: 'right' }}
            />
          </Col>
        </Row>
        {isPollingCircuitOpen && (
          <Row>
            <Col xs={24} style={{ fontSize: '12px' }}>
              Couldn&apos;t refresh recent activity
              <ButtonIcon
                style={{ marginLeft: '5px' }}
                $terciary
                size="small"
                icon="Refresh"
                aria-label="Retry loading recent activity"
                onClick={() => actions.onRetryPolling()}
              />
            </Col>
          </Row>
        )}
      </header>
      <ActivityLogContainerMobile fetching={fetching}>
        {!isEmpty && visibleItems.length > 0 && (
          <Timeline>
            {visibleItems.map((item, index) => (
              <ActivityLogItemMobile key={index} activity={item} />
            ))}
          </Timeline>
        )}
      </ActivityLogContainerMobile>
      {showActivityLogDrawer && (
        <ActivityLogDrawer activityLogDrawerVisible={showActivityLogDrawer} />
      )}
    </SectionCard>
  );
};

export default ActivityLogContentMobile;
